import type { ProficiencyLevel } from './competencies/ProficiencyLevel';
import type { EmployeeNote } from './employee-note';
import type { ThematicMeetingStatus } from './thematic-meetings';
import type { ProficiencyLevelType } from '@/constants/ProficiencyLevel';
import type { PaginatedResponse } from '@/types/api';
import type { LocalizedText } from '@/utils/localization';

/**
 * The "Croissance" tab of the performance profile: career steps, career aspirations captured in
 * "Aspirations de carrière" thematic meetings, and career journey notes. Every shape mirrors the
 * ViewModels of `PerformanceProfileController.CareerGrowth` (routes under
 * `/performance-profile/{employeeId}/…`). Enum values travel as PascalCase strings, the way
 * `ThematicMeetingTopic` and `ThematicMeetingStatus` already do.
 */

export const CAREER_STEP_KINDS = ['Past', 'Current', 'Target'] as const;

export type CareerStepKind = (typeof CAREER_STEP_KINDS)[number];

export interface CareerStep {
    id: string;
    employeeId: string;
    title: string;
    kind: CareerStepKind;
    /**
     * `YYYY-MM-DD`. For a `Target` step it is the horizon the employee aims for, and it is `null`
     * while that horizon is not decided (an aspiration that names a position creates the target
     * without a date); always set on a `Past` or `Current` step.
     */
    startDate: string | null;
    /** `YYYY-MM-DD`; `null` while the step is still ongoing. */
    endDate: string | null;
    competencyProfileId: string | null;
    /** Resolved by the API from `competencyProfileId`, so the timeline never joins client-side. */
    competencyProfileName: LocalizedText | null;
    /**
     * The competencies added to the profile for the employee, and the ones removed from it for
     * them. Only a `Target` step carries any, and only the ones that still change something today.
     */
    addedCompetencyIds: string[];
    removedCompetencyIds: string[];
}

export const TARGET_POSITION_SOURCES = ['CareerStep', 'Aspiration'] as const;

export type TargetPositionSource = (typeof TARGET_POSITION_SOURCES)[number];

/**
 * Where the employee is heading, as the API reconciles it: the more recently written of the
 * `Target` career step (the manager's plan) and the latest aspiration (what the employee said they
 * want), taken whole. The role and the competency profile always come from the same record, so the
 * competencies left to develop belong to the role shown beside them.
 */
export interface TargetPosition {
    /** `null` when the record names a competency profile without naming the role. */
    title: string | null;
    competencyProfileId: string | null;
    competencyProfileName: LocalizedText | null;
    source: TargetPositionSource;
    /** When that record was last written; what makes one of the two more recent than the other. */
    recordedAt: string;
    /** The target step's profile carries competencies added or removed for the employee. */
    isAdjusted: boolean;
}

export interface CareerStepRequest {
    title: string;
    kind: CareerStepKind;
    /** Required on a `Past` or `Current` step; the API refuses it missing there. */
    startDate: string | null;
    endDate: string | null;
    competencyProfileId: string | null;
    /**
     * The whole overlay of a `Target` step, replacing the one it had; absent, the overlay is left as
     * it is -- unless the profile or the kind changes, which clears it.
     */
    addedCompetencyIds?: string[] | null;
    removedCompetencyIds?: string[] | null;
}

export const DEVELOPMENT_PATHS = [
    'EvolveNewRole',
    'DeepenExpertise',
    'ThriveCurrentRole',
    'ExploreOtherRoles',
    'StillExploring',
] as const;

export type DevelopmentPath = (typeof DEVELOPMENT_PATHS)[number];

/** The development paths that unlock the "new role" block (desired position, importance…). */
export const NEW_ROLE_DEVELOPMENT_PATHS: ReadonlySet<DevelopmentPath> = new Set<DevelopmentPath>([
    'EvolveNewRole',
    'ExploreOtherRoles',
]);

export const ACCEPTS_NEW_RESPONSIBILITIES_VALUES = ['Yes', 'No', 'Partial'] as const;

export type AcceptsNewResponsibilities = (typeof ACCEPTS_NEW_RESPONSIBILITIES_VALUES)[number];

export const DEPARTURE_RISKS = ['Low', 'Moderate', 'High'] as const;

export type DepartureRisk = (typeof DEPARTURE_RISKS)[number];

export const MOTIVATION_FACTORS = [
    'ProfessionalGrowth',
    'TechnicalChallenges',
    'HigherCompensation',
    'Autonomy',
    'Leadership',
    'WorkLifeBalance',
    'Recognition',
    'Impact',
] as const;

export type MotivationFactor = (typeof MOTIVATION_FACTORS)[number];

export const SUPPORT_NEEDS = [
    'FormalTraining',
    'InternalMentoring',
    'ProfessionalCoaching',
    'MoreManagerMeetings',
    'MoreFrequentFeedback',
    'StretchProject',
    'CrossFunctionalCommittee',
    'ProgressiveDelegation',
    'Shadowing',
    'LeadershipVisibility',
    'TemporaryAssignment',
    'IndustryEvents',
    'PeerLearningGroup',
    'SelfPacedResources',
    'DevelopmentBudget',
    'ProtectedTime',
    'CareerPathClarity',
    'TrainOrMentorOthers',
    'NoSupportNeeded',
] as const;

export type SupportNeed = (typeof SUPPORT_NEEDS)[number];

export const GROWTH_BLOCKERS = [
    'WorkloadAvailability',
    'SkillsToConsolidate',
    'Confidence',
    'PersonalConstraints',
    'FewOrgOpportunities',
    'NoneParticular',
] as const;

export type GrowthBlocker = (typeof GROWTH_BLOCKERS)[number];

/** The importance levels an employee can pick, in order. The dictionary names each one. */
export const IMPORTANCE_LEVELS = [1, 2, 3, 4, 5] as const;

export type ImportanceLevel = (typeof IMPORTANCE_LEVELS)[number];

export const IMPORTANCE_LEVEL_MAX = IMPORTANCE_LEVELS.length;

/** The answers captured by one "Aspirations de carrière" meeting. */
export interface CareerAspiration {
    id: string;
    employeeId: string;
    thematicMeetingId: string | null;
    desiredPositionTitle: string | null;
    desiredCompetencyProfileId: string | null;
    /** Resolved by the API from `desiredCompetencyProfileId`. */
    desiredCompetencyProfileName: LocalizedText | null;
    /** 1 to `IMPORTANCE_LEVEL_MAX`. */
    importanceLevel: number | null;
    acceptsNewResponsibilities: AcceptsNewResponsibilities | null;
    timeAlignmentNotes: string | null;
    motivationFactors: MotivationFactor[];
    developmentPath: DevelopmentPath | null;
    supportNeeds: SupportNeed[];
    supportNeedsOther: string | null;
    growthBlockers: GrowthBlocker[];
    blockersNotes: string | null;
    /** Confidential: shown to the employee's management line, never to the employee. */
    departureRisk: DepartureRisk | null;
    createdAt: string;
    updatedAt: string;
}

/**
 * One entry of the aspiration history menu: an aspiration that was recorded (with or without a
 * meeting), or a career meeting nothing has been recorded in yet -- pending, so the manager sees
 * the meeting exists and can fill it in.
 */
export interface CareerAspirationHistoryEntry {
    /** `null` while the entry is pending. */
    aspirationId: string | null;
    /** `null` when the aspiration was recorded from the tab, without a meeting. */
    thematicMeetingId: string | null;
    meetingStatus: ThematicMeetingStatus | null;
    meetingScheduledAt: string | null;
    /** When the aspiration was recorded, or when the pending meeting was created. */
    createdAt: string;
    /** Last edit of the aspiration; equals `createdAt` for a pending entry. Sort key, newest first. */
    updatedAt: string;
    isPending: boolean;
}

/** An aspiration's answers, all a replacement sends: the meeting it was recorded in never changes. */
export type CareerAspirationUpdateRequest = Omit<
    CareerAspiration,
    | 'id'
    | 'employeeId'
    | 'thematicMeetingId'
    | 'desiredCompetencyProfileName'
    | 'createdAt'
    | 'updatedAt'
>;

/** A new aspiration's answers, with the career meeting it is recorded in, if any. */
export type CareerAspirationCreateRequest = CareerAspirationUpdateRequest &
    Pick<CareerAspiration, 'thematicMeetingId'>;

/** The growth journey shows its `Career` notes five at a time. */
export const JOURNEY_NOTES_PAGE_SIZE = 5;

/**
 * One competency of the employee's desired profile with the level it requires and the level last
 * assessed for them, so the tab can show what is left to develop for the next role. `proficiency`
 * is `null` when the competency was never assessed.
 */
export interface NextLevelSkill {
    competencyId: string;
    name: LocalizedText;
    description: LocalizedText;
    expectedLevel: ProficiencyLevelType;
    proficiency: ProficiencyLevel | null;
}

/** Everything the tab needs for its first paint, in one call (`GET …/growth`). */
export interface EmployeeGrowth {
    employeeId: string;
    careerSteps: CareerStep[];
    /** The most recent aspiration, or `null` when none has been recorded yet. */
    currentAspiration: CareerAspiration | null;
    /** Most recent first. */
    aspirationHistory: CareerAspirationHistoryEntry[];
    /** First page of the `Career` notes; the notes route serves the next pages. */
    notes: PaginatedResponse<EmployeeNote>;
    /** Where the employee is heading, `null` when neither record names a role or a profile. */
    targetPosition: TargetPosition | null;
    /**
     * The competencies of the profile of `targetPosition`, each with the level it requires and the
     * level last assessed; empty without a target position or when it has no profile.
     */
    nextLevelSkills: NextLevelSkill[];
}
